import ApiSmile from '@/api/api-smile';
import ActionArea from '@/component/action-area';
import {ActionButton} from '@/component/action-area/action-area';
import DataRow from '@/component/data-row';
import PageContainer from '@/component/page-container';
import LoadingUtil from '@/util/loading-util';
import MessageUtil from '@/util/message-util';
import Utils from '@/util/utils';
import {DatePicker, Form, InputNumber, Modal} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import moment, {Moment} from 'moment';
import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router';
import {FundData} from '../types';
import './fund-detail.scss';

interface CreateData {
    description: string;
    tradeAmount: number;
    tradeProfit: number;
    tradeTime: Moment;
}

const FundDetail = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState<FundData>();
    const [isEdit, setIsEdit] = useState(false);
    const isNew = !(Number(id) > 0);
    const [form] = Form.useForm();

    useEffect(() => {
        if (!Utils.isNaturalNumber(id)) {
            navigate('/', {replace: true});
            return;
        }

        const load = async () => {
            LoadingUtil.show();
            const response: any = await ApiSmile.getFundDetail(id || '');
            if (response?.data?.code === 0) {
                setData(response.data.data);
            } else {
                const msg = response?.data?.msg || '网络请求失败';
                MessageUtil.error(msg);
            }
            LoadingUtil.hide();
        };

        if (Number(id) > 0) {
            load();
        }
    }, [navigate, id]);

    useEffect(() => {
        if (isNew) {
            setIsEdit(true);
            form.setFieldsValue({
                description: '',
                tradeAmount: 0,
                tradeProfit: 0,
                tradeTime: moment(),
            });
        }
    }, [isNew, form]);

    const onUpdateData = () => {
        const value = form.getFieldsValue();
        const payload = {
            description: value?.description || '',
            tradeTime: value?.tradeTime,
        };
        if (!payload.tradeTime) {
            MessageUtil.error('交易时间不能为空');
            return false;
        }
        const update = async () => {
            LoadingUtil.show();
            const response: any = await ApiSmile.updateFundDetail(id || '', payload);
            if (response?.data?.code === 0) {
                setData(response.data.data);
                onCancelClick();
                MessageUtil.info('保存成功');
            } else {
                const msg = response?.data?.msg || '网络请求失败';
                MessageUtil.error(msg);
            }
            LoadingUtil.hide();
        };

        Modal.confirm({
            title: '确定更新吗？',
            content: (
                <div>
                    <div>{`时间：${payload.tradeTime.format('YYYY-MM-DD')}`}</div>
                    <div>{`描述：${payload.description}`}</div>
                </div>
            ),
            onOk: update,
        });
    };

    const validateCreateData = (payload: CreateData) => {
        if (!payload.tradeTime) {
            MessageUtil.error('交易时间不能为空');
            return false;
        }
        if (!Number.isFinite(payload.tradeAmount)) {
            MessageUtil.error('交易金额数据格式错误');
            return false;
        }
        if (!Number.isFinite(payload.tradeProfit)) {
            MessageUtil.error('交易收益数据格式错误');
            return false;
        }
        if (payload.tradeAmount === 0 && payload.tradeProfit === 0) {
            MessageUtil.error('交易金额和交易收益不能同时为零');
            return false;
        }
        return true;
    };

    const onCreateData = () => {
        const value = form.getFieldsValue();
        const payload: CreateData = {
            description: value?.description || '',
            tradeAmount: Number(value?.tradeAmount) || 0,
            tradeProfit: Number(value?.tradeProfit) || 0,
            tradeTime: value?.tradeTime,
        };
        if (!validateCreateData(payload)) {
            return;
        }

        const create = async () => {
            LoadingUtil.show();
            const response: any = await ApiSmile.createFundDetail(payload);
            if (response?.data?.code === 0) {
                navigate(`/fund/${response.data.data.id}`, {replace: true});
                onResetEdit();
                MessageUtil.info('保存成功');
            } else {
                const msg = response?.data?.msg || '网络请求失败';
                MessageUtil.error(msg);
            }
            LoadingUtil.hide();
        };

        Modal.confirm({
            title: '确定添加吗？',
            content: (
                <div>
                    <div>{`时间：${payload.tradeTime.format('YYYY-MM-DD')}`}</div>
                    <div>{`金额：${payload.tradeAmount}`}</div>
                    <div>{`收益：${payload.tradeProfit}`}</div>
                    <div>{`描述：${payload.description}`}</div>
                </div>
            ),
            onOk: create,
        });
    };

    const onSaveClick = () => {
        if (isNew) {
            onCreateData();
        } else {
            onUpdateData();
        }
    };

    const onResetEdit = () => {
        setIsEdit(false);
        form.resetFields();
    };

    const onCancelClick = () => {
        if (isNew) {
            navigate('/fund', {replace: true});
        } else {
            onResetEdit();
        }
    };

    const onEditClick = () => {
        setIsEdit(true);
        form.setFieldsValue({...data, tradeTime: moment(data?.tradeTime || 0)});
    };

    const getButtons = () => {
        const buttons: ActionButton[] = [];
        if (isEdit) {
            buttons.push({text: '取消', onClick: onCancelClick});
            buttons.push({text: '保存', onClick: onSaveClick, type: 'primary'});
        } else {
            if (data) {
                buttons.push({text: '编辑', onClick: onEditClick});
            }
        }
        return buttons;
    };

    return (
        <PageContainer className="page-fund-detail">
            <ActionArea buttons={getButtons()} />
            <Form form={form}>
                <DataRow title="交易时间">
                    {isEdit ? (
                        <Form.Item name={'tradeTime'}>
                            <DatePicker />
                        </Form.Item>
                    ) : (
                        Utils.formatDate(data?.tradeTime || 0)
                    )}
                </DataRow>
                <DataRow title="交易金额">
                    {isEdit && isNew ? (
                        <Form.Item name={'tradeAmount'}>
                            <InputNumber className="data-desc price-input" stringMode step={'0.01'} />
                        </Form.Item>
                    ) : (
                        Utils.formatPrice(data?.tradeAmount || 0)
                    )}
                </DataRow>
                <DataRow title="交易收益">
                    {isEdit && isNew ? (
                        <Form.Item name={'tradeProfit'}>
                            <InputNumber className="data-desc price-input" stringMode step={'0.01'} />
                        </Form.Item>
                    ) : (
                        Utils.formatPrice(data?.tradeProfit || 0)
                    )}
                </DataRow>
                <DataRow title="资金池余额">{isEdit && isNew ? '--' : Utils.formatPrice(data?.fundPoolBalance || 0)}</DataRow>
                <DataRow title="累计收益">{isEdit && isNew ? '--' : Utils.formatPrice(data?.totalProfit || 0)}</DataRow>
                <DataRow title="描述">
                    {isEdit ? (
                        <Form.Item name={'description'}>
                            <TextArea rows={4} className="data-desc" />
                        </Form.Item>
                    ) : (
                        data?.description || ''
                    )}
                </DataRow>
            </Form>
        </PageContainer>
    );
};

export default FundDetail;
